import { _isNil, _notNil } from '@/littledash';
import type {
  AgentSessionV1,
  SendBundleDetectionResult,
  SendBundleValidationResult,
  SendExecutionResult,
  SendInputFailureResult,
  SendResultType,
} from '@/model/Agent.model';

type SchemaVersionOf<Type extends SendResultType> = Extract<
  SendExecutionResult,
  { result_type: Type }
>['schema_version'];

/**
 * The shape version of each SEND result this build reads. Typed from the generated schemas, so a
 * server that raises a version fails the build here until the UI reads the new shape.
 */
const SUPPORTED_SCHEMA_VERSIONS: { readonly [Type in SendResultType]: SchemaVersionOf<Type> } = {
  bundle_validation: 1,
  bundle_detection: 1,
  input_failure: 1,
};

const isSendResultType = (value: unknown): value is SendResultType =>
  typeof value === 'string' && Object.hasOwn(SUPPORTED_SCHEMA_VERSIONS, value);

/**
 * Narrow an execution result to the SEND result it declares itself to be, from `result_type` and
 * `schema_version` alone, never from which other fields it carries. Null for anything else: a
 * result stored before results named their type, or at a version this build does not read.
 *
 * One allowance, as the server makes it: an input failure named its type before results had a
 * version, in the shape that is now version 1, so one stored without a version is read as version 1.
 */
export const asSendExecutionResult = (value: unknown): SendExecutionResult | null => {
  if (typeof value !== 'object' || _isNil(value) || !('result_type' in value)) {
    return null;
  }
  const resultType = value.result_type;
  if (!isSendResultType(resultType)) {
    return null;
  }
  if (!('schema_version' in value)) {
    return resultType === 'input_failure' ? ({ ...value, schema_version: 1 } as SendExecutionResult) : null;
  }
  return SUPPORTED_SCHEMA_VERSIONS[resultType] === value.schema_version ? (value as SendExecutionResult) : null;
};

/** What a SEND session's executions hold, as far as the results modals are concerned. */
export type SendSessionResult =
  | { kind: 'input_failure'; result: SendInputFailureResult }
  | { kind: 'validation'; result: SendBundleValidationResult }
  | { kind: 'earlier_version' }
  | { kind: 'none' };

/**
 * The one place a SEND session's results are told apart. A session carries a detection and a
 * validation execution, returned newest-first, so each is found by its `result_type`, never by its
 * position or its shape.
 *
 * An input failure wins, because "nothing was checked" must never read as "nothing was wrong". A
 * session holding a result this build cannot identify, and none it can, was produced by an earlier
 * version: it is reported as such, never rendered, so an old shape can never be shown as a clean
 * result.
 */
export const sendSessionResult = (session: AgentSessionV1 | null): SendSessionResult => {
  const { inputFailure, validation, holdsAnUnidentifiedResult } = (session?.executions ?? []).reduce<{
    inputFailure?: SendInputFailureResult;
    validation?: SendBundleValidationResult;
    holdsAnUnidentifiedResult: boolean;
  }>(
    (found, execution) => {
      if (_isNil(execution?.result)) {
        return found;
      }
      const result = asSendExecutionResult(execution.result);
      if (_isNil(result)) {
        return { ...found, holdsAnUnidentifiedResult: true };
      }
      if (result.result_type === 'input_failure') {
        return { ...found, inputFailure: found.inputFailure ?? result };
      }
      if (result.result_type === 'bundle_validation') {
        return { ...found, validation: found.validation ?? result };
      }
      return found;
    },
    { holdsAnUnidentifiedResult: false }
  );

  if (_notNil(inputFailure)) {
    return { kind: 'input_failure', result: inputFailure };
  }
  if (_notNil(validation)) {
    return { kind: 'validation', result: validation };
  }
  return holdsAnUnidentifiedResult ? { kind: 'earlier_version' } : { kind: 'none' };
};

/**
 * The detection result a SEND session holds, found by its `result_type` like every other result, or
 * null when it holds none this build reads.
 */
export const sendDetectionResultOf = (session: AgentSessionV1 | null): SendBundleDetectionResult | null =>
  (session?.executions ?? []).reduce<SendBundleDetectionResult | null>((found, execution) => {
    if (_notNil(found) || _isNil(execution?.result)) {
      return found;
    }
    const result = asSendExecutionResult(execution.result);
    return result?.result_type === 'bundle_detection' ? result : null;
  }, null);
